import { DEFAULT_LOCALE, isSupportedLocale, type Locale, SUPPORTED_LOCALES } from './constants.ts';

export interface DetectOptions {
  /** Candidate locales in priority order. Defaults to {@link SUPPORTED_LOCALES}. */
  readonly supported?: readonly Locale[];
  /** Returned when nothing matches. Defaults to {@link DEFAULT_LOCALE}. */
  readonly fallback?: Locale;
}

/**
 * Resolve the best supported locale from an ordered preference list (e.g.
 * `navigator.languages`). Each preference is normalised (lowercased,
 * region-stripped: `en-US` → `en`) and matched against the supported set;
 * the first hit wins. Falls back to the default when none match.
 *
 * The preference list is a parameter (never a global) so this runs in Bun
 * tests — use {@link detectFromBrowser} for the `navigator`-backed wrapper.
 */
export function detectLocale(preferences: readonly string[], options: DetectOptions = {}): Locale {
  const supported = options.supported ?? SUPPORTED_LOCALES;
  const fallback = options.fallback ?? DEFAULT_LOCALE;

  for (const pref of preferences) {
    const base = normalize(pref);
    if (!base) continue;
    const match = supported.find((locale) => locale === base);
    if (match) return match;
  }
  return fallback;
}

/**
 * `navigator`-backed convenience wrapper. Reads `navigator.languages` (falling
 * back to `navigator.language`) only when `navigator` is defined, so it is safe
 * to import in non-browser runtimes.
 */
export function detectFromBrowser(options: DetectOptions = {}): Locale {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const preferences: readonly string[] = nav?.languages?.length
    ? nav.languages
    : nav?.language
      ? [nav.language]
      : [];
  return detectLocale(preferences, options);
}

/** Lowercase and strip region/script subtags: `en-US` → `en`, `zh-Hant` → `zh`. */
function normalize(tag: string): string {
  return tag.trim().toLowerCase().split('-')[0] ?? '';
}

// Re-exported for callers that want to validate a persisted override.
export { isSupportedLocale };
